import type {
  AttikNativeReportPayload,
  AttikReportObservation,
  AttikReportSection,
} from './attikReportTypes';
import { observationDisplayText } from './attikReportTypes';
import {
  defectLevelRank,
  resolveDefectLevels,
  resolveObservationDefectLevelId,
  type ResolvedDefectLevels,
} from '@attik/types/defectLevels.types';

export function getResolvedDefectLevels(
  levels?: Parameters<typeof resolveDefectLevels>[0],
): ResolvedDefectLevels {
  return resolveDefectLevels(levels);
}

export function defectLevelKey(
  obs: AttikReportObservation,
  resolved: ResolvedDefectLevels,
): string {
  return resolveObservationDefectLevelId(obs.defectLevel, resolved);
}

/** @deprecated Use defectLevelKey */
export const defectSeverityKey = defectLevelKey;

export function countDefectLevels(
  payload: AttikNativeReportPayload,
  resolved: ResolvedDefectLevels,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const level of resolved.levels) {
    counts[level.id] = 0;
  }

  for (const section of payload.sections) {
    for (const sub of section.subsections) {
      for (const obs of sub.observations) {
        if (obs.commentType !== 'defect') continue;
        const id = defectLevelKey(obs, resolved);
        counts[id] = (counts[id] ?? 0) + 1;
      }
    }
  }

  return counts;
}

/** @deprecated Use countDefectLevels */
export const countSeverities = countDefectLevels;

export function sectionDefectCount(section: AttikReportSection): number {
  return section.subsections.reduce(
    (n, sub) => n + sub.observations.filter((o) => o.commentType === 'defect').length,
    0,
  );
}

export function sectionWorstDefectLevel(
  section: AttikReportSection,
  resolved: ResolvedDefectLevels,
): string | null {
  let worstId: string | null = null;
  let worstRank = -1;

  for (const sub of section.subsections) {
    for (const obs of sub.observations) {
      if (obs.commentType !== 'defect') continue;
      const id = defectLevelKey(obs, resolved);
      const rank = defectLevelRank(id, resolved);
      if (rank > worstRank) {
        worstRank = rank;
        worstId = id;
      }
    }
  }

  return worstId;
}

/** @deprecated Use sectionWorstDefectLevel */
export const sectionWorstSeverity = sectionWorstDefectLevel;

export function firstLineTitle(text: string, maxLen = 80): string {
  const line = text.split('\n')[0]?.trim() ?? '';
  if (line.length <= maxLen) return line || 'Finding';
  return `${line.slice(0, maxLen - 1)}…`;
}

export function observationTitle(obs: AttikReportObservation): string {
  return firstLineTitle(observationDisplayText(obs));
}

export function observationCardTitle(
  obs: AttikReportObservation,
  templateTitleById?: Map<string, string>,
): string {
  const persisted = obs.title?.trim();
  if (persisted) return persisted;
  const templateId = obs.templateCommentId?.trim();
  if (templateId) {
    const templateTitle = templateTitleById?.get(templateId)?.trim();
    if (templateTitle) {
      const loc = obs.location?.trim();
      return loc ? `${templateTitle} — ${loc}` : templateTitle;
    }
  }
  const text = observationDisplayText(obs);
  return text ? observationTitle(obs) : 'Untitled';
}
